import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { AnimatePresence, motion } from 'framer-motion';
import Icon from '../components/Icon';
import ProjectCard from '../components/ProjectCard';
import ProjectShowcase from '../components/ProjectShowcase';
import ProjectLightbox from '../components/ProjectLightbox';
import { PageHero, SectionHead, StatsRow, CTABand } from '../components/ui';
import SplitText from '../motion/SplitText';
import TiltCard from '../motion/TiltCard';
import { Reveal, Stagger, StaggerItem, EASE } from '../motion/Reveal';
import useProjects from '../hooks/useProjects';
import { API_BASE } from '../config';
import { PORTFOLIO_PROMISES, projectMedia } from '../data/site';

const PF_STATS = [
  { end: 11, suffix: '', label: 'Projects listed', sub: 'Active & completed' },
  { end: 10, suffix: '+', label: 'Years experience', sub: 'Civil construction PMC' },
  { end: 97, suffix: '%', label: 'On-time delivery', sub: 'Schedule discipline' },
  { end: 100, suffix: '%', label: 'Client commitment', sub: 'Quality & safety first' },
];

function NotifyForm() {
  const [email, setEmail] = useState('');
  const [state, setState] = useState('idle');
  const submit = async (e) => {
    e.preventDefault();
    setState('sending');
    try { await axios.post(`${API_BASE}/subscribe`, { email, source: 'portfolio' }); setState('done'); } catch { setState('error'); }
  };
  return (
    <AnimatePresence mode="wait">
      {state === 'done' ? (
        <motion.div key="ok" className="alert alert-success" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <Icon name="checkCircle" size={20} />You're on the list — we'll email you when case studies go live.
        </motion.div>
      ) : (
        <motion.form key="form" className="notify-form" onSubmit={submit} exit={{ opacity: 0, y: -10 }}>
          <input className="input" type="email" required placeholder="your@email.com" value={email} onChange={(e) => setEmail(e.target.value)} aria-label="Email address" />
          <button className="btn btn-primary" disabled={state === 'sending'}>{state === 'sending' ? <span className="spinner" /> : <>Notify me <span className="arrow"><Icon name="bell" size={14} /></span></>}</button>
          {state === 'error' && <p className="field-error" style={{ width: '100%' }}>Couldn't save your email. Please try again.</p>}
          <p style={{ fontSize: 13, width: '100%', color: '#64748B' }}>No spam. One email at launch.</p>
        </motion.form>
      )}
    </AnimatePresence>
  );
}

export default function Portfolio() {
  const { projects, loading } = useProjects();
  const [filter, setFilter] = useState('All');
  const [open, setOpen] = useState(null);
  const categories = useMemo(() => ['All', ...new Set(projects.map((p) => p.category).filter(Boolean))], [projects]);
  const count = (c) => (c === 'All' ? projects.length : projects.filter((p) => p.category === c).length);
  // projects with photos first, then the rest
  const sorted = useMemo(() => [...projects].sort((a, b) => (projectMedia(b) ? 1 : 0) - (projectMedia(a) ? 1 : 0)), [projects]);
  const shown = filter === 'All' ? sorted : sorted.filter((p) => p.category === filter);
  const withPhotos = projects.filter((p) => projectMedia(p));

  return (
    <>
      <PageHero
        title="Our work, built|on *precision*"
        lead="Residential, commercial and site layout projects across Tamil Nadu — each one a story of precision planning, disciplined execution and genuine Blue Block PMC oversight."
        images={['/projects/mahendhran-residence-1.webp', '/projects/skyrand-head-office-1-sm.webp']} />

      {/* ═══ FEATURED SHOWCASE ═══ */}
      <section className="section" style={{ paddingBottom: 60 }}>
        <div className="container">
          <div className="head-row">
            <SectionHead num="01" kicker="Featured work" title="Projects we're|*proud* of" lead="Hover a project to preview it — click to open the full gallery and project details." />
            <span className="sc-hint"><Icon name="eye" size={16} />{withPhotos.length} featured projects</span>
          </div>
          <Reveal from="up"><ProjectShowcase projects={projects} onOpen={setOpen} /></Reveal>
        </div>
      </section>

      {/* ═══ ALL PROJECTS ═══ */}
      <section className="section section-alt" style={{ paddingTop: 100 }}>
        <div className="container">
          <div style={{ textAlign: 'center' }}>
            <SectionHead center num="02" kicker="All projects" title="Filter by *sector*" lead="Every project managed by Blue Block PMC — with site photos where available." />
            <Reveal>
              <div className="filters" role="tablist">
                {categories.map((c) => (
                  <button key={c} role="tab" aria-selected={filter === c} className={`filter-btn${filter === c ? ' on' : ''}`} onClick={() => setFilter(c)}>
                    <motion.span className="filter-pill" initial={false} animate={{ opacity: filter === c ? 1 : 0, scale: filter === c ? 1 : 0.85 }} transition={{ type: 'spring', stiffness: 380, damping: 30 }} />
                    {c}<span>{count(c)}</span>
                  </button>
                ))}
              </div>
            </Reveal>
            {loading && <p className="muted" style={{ marginTop: 18 }}>Loading latest projects…</p>}
          </div>
          <motion.div className="pgrid" layout>
            <AnimatePresence mode="popLayout">
              {shown.map((p, i) => (
                <motion.div key={p.id} layout className={i === 0 && filter === 'All' && projectMedia(p) ? 'pg-big' : ''}
                  initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.3 } }} transition={{ duration: 0.6, delay: Math.min(i, 8) * 0.05, ease: EASE }}>
                  <ProjectCard project={p} onOpen={setOpen} big={i === 0 && filter === 'All'} />
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      <section className="section-sm"><div className="container"><StatsRow items={PF_STATS} /></div></section>

      <section className="section-sm">
        <div className="container">
          <Reveal from="zoom" className="notify">
            <div className="aurora" /><div className="grid-bg" />
            <div style={{ position: 'relative' }}>
              <span className="kicker"><b>03</b>Case studies</span>
              <SplitText as="h2" className="h2" inView text="Full case studies|*publishing soon*" />
              <p style={{ marginTop: 14 }}>Detailed write-ups with outcomes, site data and client references are on the way.</p>
            </div>
            <div style={{ position: 'relative' }}><NotifyForm /></div>
          </Reveal>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <SectionHead center num="04" kicker="In documentation" title="What our portfolio|will *show*" lead="Every project reflects genuine Blue Block PMC oversight — quality, timeline and cost efficiency." />
          <Stagger className="grid grid-3" gap={0.08}>
            {PORTFOLIO_PROMISES.map((w) => (
              <StaggerItem key={w.title}>
                <TiltCard className="card" max={8} style={{ height: '100%' }}>
                  <div className="icon-box"><Icon name={w.icon} size={24} /></div>
                  <h3 className="h3" style={{ marginBottom: 10 }}>{w.title}</h3>
                  <p>{w.desc}</p>
                </TiltCard>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <CTABand title="Have a project|in *mind?*" text="Join our growing list of residential, commercial and site layout projects managed with Blue Block PMC discipline." />
      <ProjectLightbox project={open} onClose={() => setOpen(null)} />
    </>
  );
}
